/**
 * Linking a second device to an existing account.
 *
 * All devices of one account share a single identity key. That is what keeps
 * the safety number meaningful — a contact verifies *you*, not each of your
 * devices — but it means the private key has to travel from the primary device
 * to the new one.
 *
 * It travels sealed to a one-time X25519 key that the new device generates and
 * shows on its own screen. The server relays the ciphertext and never sees the
 * ephemeral private key, so it cannot open the payload even though it stores it.
 *
 * The channel that carries the ephemeral *public* key — a QR code, or a short
 * string the user types across — is the trust anchor. Whoever controls that
 * channel gets the identity, which is exactly the assumption Signal makes for
 * its own linked devices.
 */
import {
  aeadDecrypt,
  aeadEncrypt,
  dh,
  fromB64,
  fromUtf8,
  generateDHKeyPair,
  kdf,
  KEY_LEN,
  NONCE_LEN,
  randomBytes,
  sha256,
  toB64,
  utf8,
  type KeyPair,
} from '@vav/protocol';

const LINK_INFO = 'VAV-DeviceLink-v1';

/** What the primary seals and the new device unseals. */
export interface LinkPayload {
  userId: string;
  username: string;
  displayName: string;
  /** The account identity keypair, base64. */
  identityPriv: string;
  identityPub: string;
  serverUrl: string;
}

export interface LinkOffer {
  provisionId: string;
  ephemeral: KeyPair;
  /** What goes in the QR code. */
  code: string;
  expiresAt: number;
}

/** Encode an offer compactly enough for a QR code. */
export function encodeOffer(provisionId: string, ephemeralPub: Uint8Array): string {
  return `vav://link?p=${provisionId}&k=${encodeURIComponent(toB64(ephemeralPub))}`;
}

export function decodeOffer(code: string): { provisionId: string; ephemeralPub: Uint8Array } {
  const url = new URL(code.trim());
  // The QR carries an ordinary https link so a phone's native camera opens
  // the app instead of erroring on an unknown scheme; the offer rides inside
  // it as ?approve=<vav://…>. Unwrap before reading.
  const wrapped = url.searchParams.get('approve');
  if (wrapped) return decodeOffer(decodeURIComponent(wrapped));
  const provisionId = url.searchParams.get('p');
  const key = url.searchParams.get('k');
  if (!provisionId || !key) throw new Error('not a valid linking code');
  const ephemeralPub = fromB64(key);
  if (ephemeralPub.length !== 32) throw new Error('linking code has a malformed key');
  return { provisionId, ephemeralPub };
}

/** The scannable form: an https link the native camera will actually open. */
export function offerLink(origin: string, code: string): string {
  return `${origin.replace(/\/$/, '')}/?approve=${encodeURIComponent(code)}`;
}

/**
 * A six-digit check the user compares on both screens before approving.
 *
 * Derived from the ephemeral key, so a swapped QR produces different digits on
 * the two devices. Without this step a malicious page could show its own code
 * and receive the identity instead.
 */
export function linkVerificationCode(ephemeralPub: Uint8Array): string {
  const digest = sha256(new Uint8Array([...utf8(LINK_INFO), ...ephemeralPub]));
  const value = ((digest[0] << 16) | (digest[1] << 8) | digest[2]) % 1_000_000;
  return String(value).padStart(6, '0');
}

function linkKey(shared: Uint8Array, ephemeralPub: Uint8Array): Uint8Array {
  return kdf(shared, ephemeralPub, LINK_INFO, KEY_LEN);
}

/** Primary side: seal the account identity to the new device's ephemeral key. */
export function sealLinkPayload(
  payload: LinkPayload,
  ephemeralPub: Uint8Array,
): { senderPub: string; ciphertext: string } {
  const sender = generateDHKeyPair();
  const key = linkKey(dh(sender.priv, ephemeralPub), ephemeralPub);
  const nonce = randomBytes(NONCE_LEN);
  // The recipient's key is authenticated, so a payload cannot be replayed at a
  // different linking session even if the ciphertext is captured.
  const sealed = aeadEncrypt(key, nonce, utf8(JSON.stringify(payload)), ephemeralPub);
  return { senderPub: toB64(sender.pub), ciphertext: `${toB64(nonce)}.${toB64(sealed)}` };
}

/** New-device side: open what the primary sealed. */
export function openLinkPayload(
  ephemeral: KeyPair,
  senderPub: string,
  ciphertext: string,
): LinkPayload {
  const [nonce, sealed] = ciphertext.split('.');
  if (!sealed) throw new Error('malformed link payload');
  const key = linkKey(dh(ephemeral.priv, fromB64(senderPub)), ephemeral.pub);
  const plaintext = aeadDecrypt(key, fromB64(nonce), fromB64(sealed), ephemeral.pub);
  return JSON.parse(fromUtf8(plaintext)) as LinkPayload;
}
